// Copyright © 2022 Vewd Software AS.
//
// This file is part of Vewd Cloud,
// and includes Vewd Confidential Information.
// Distribution is strictly prohibited without Vewd's written consent.
import get from "lodash-es/get";
import intersection from "lodash-es/intersection";

import { ROLES } from "containers/Permissions/roles";
import { NO_VALUE_PLACEHOLDER } from "src/constants";

export const getUserRoles = (userGrants, roles) => {
  return userGrants
    .filter((g) => {
      return g.role !== ROLES.organization.member;
    })
    .map((grant) => ({
      role:
        (roles.find(({ id }) => id === grant.role) || {}).name || grant.role,
    }));
};

export const getUserRolesString = (userRoles) => {
  if (!userRoles || userRoles.length === 0) {
    return NO_VALUE_PLACEHOLDER;
  }

  return userRoles.map(({ role }) => role).join(", ");
};

export const getFullName = ({ given_name, family_name }) =>
  [given_name, family_name].join(" ") || NO_VALUE_PLACEHOLDER;

const createDescription = (activeOrganization, userRoles = []) => {
  if (!activeOrganization) {
    return "";
  }
  return `${getUserRolesString(userRoles)} @ ${activeOrganization.name}`;
};

export const getUserDetailsData = (user, activeOrganization, userRoles) => ({
  name:
    [user.given_name, user.family_name].join(" ").trim() ||
    NO_VALUE_PLACEHOLDER,
  email: user.email,
  logo: user.avatar,
  description: createDescription(activeOrganization, userRoles),
});

export const getOrganizationNamesString = (organizationNames) => {
  if (!organizationNames || organizationNames.length === 0) {
    return NO_VALUE_PLACEHOLDER;
  }

  return organizationNames.join(", ");
};

const isAllowedToEditUserInOrganization = (user, activeOrganization) => {
  const authorizations = get(user, "authorizations", []);
  const activeOrganizationId = get(activeOrganization, "id");
  const isInActiveOrg = authorizations.find(
    (auth) => auth.organization === activeOrganizationId
  );

  const allowedRoles = [ROLES.organization.roleManager];
  const roles = get(activeOrganization, "roles", []);
  const hasRequiredRole = intersection(allowedRoles, roles).length > 0;

  return Boolean(isInActiveOrg) && hasRequiredRole;
};

export const isAllowedToEditUser =
  (user, allowEditSelf = true) =>
  ({ activeOrganization, currentUser }) => {
    const userId = get(user, "id");
    const editsSelf = allowEditSelf && userId === currentUser.id;
    const roles = get(activeOrganization, "roles", []);
    const isUserAdmin = roles.some(
      (role) => role === ROLES.administrator.userAdmin
    );

    return (
      editsSelf ||
      isAllowedToEditUserInOrganization(user, activeOrganization) ||
      isUserAdmin
    );
  };
